/**
 * useMapInteractions — Click handlers, viewport data fetching, and cursor management for MapboxMap.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { getConditionColor } from '@/utils/mapConstants';

export default function useMapInteractions({
  mapRef,
  destinations,
  onDestinationSelect,
  onWaterwayClick,
  showStreamGauges,
  showPOIs,
  selectedPOITypes,
  setPopupInfo,
  setRiverPanelData,
}) {
  // Viewport-based loading for POIs (includes stream gauges)
  const [viewportPOIs, setViewportPOIs] = useState([]);
  const [poisLoading, setPoisLoading] = useState(false);
  const lastBoundsRef = useRef(null);
  const lastPOITypesRef = useRef(null);
  const fetchTimeoutRef = useRef(null);

  // Stream gauge popup loading state (for lazy-loading conditions)
  const [gaugeConditionsLoading, setGaugeConditionsLoading] = useState(false);
  const [gaugeConditionsCache, setGaugeConditionsCache] = useState({});

  // River data loading state (for lazy-loading aggregated river info)
  const [riverDataLoading, setRiverDataLoading] = useState(false);
  const [riverDataCache, setRiverDataCache] = useState({});

  // Fetch POIs (including stream gauges) by bounding box
  const fetchViewportData = useCallback(async (bounds, zoom) => {
    if (zoom < 6) {
      setViewportPOIs([]);
      return;
    }

    const { _sw, _ne } = bounds;
    const boundsKey = `${_sw.lat.toFixed(3)},${_sw.lng.toFixed(3)},${_ne.lat.toFixed(3)},${_ne.lng.toFixed(3)}`;

    // Build POI types list
    const typesToFetch = [...selectedPOITypes];
    if (showStreamGauges && !typesToFetch.includes('stream_gauge')) {
      typesToFetch.push('stream_gauge');
    }

    const poiTypesKey = typesToFetch.sort().join(',');

    const boundsChanged = lastBoundsRef.current !== boundsKey;
    const poiTypesChanged = lastPOITypesRef.current !== poiTypesKey;

    lastBoundsRef.current = boundsKey;
    lastPOITypesRef.current = poiTypesKey;

    const shouldFetch = (showPOIs && selectedPOITypes.length > 0) || showStreamGauges;

    if (shouldFetch && typesToFetch.length > 0 && (boundsChanged || poiTypesChanged)) {
      setPoisLoading(true);
      try {
        const response = await axios.get(`${API}/pois/bounds`, {
          params: {
            north: _ne.lat,
            south: _sw.lat,
            east: _ne.lng,
            west: _sw.lng,
            types: typesToFetch.join(','),
            limit: 1500
          },
          timeout: 15000
        });
        setViewportPOIs(response.data || []);
      } catch (error) {
      } finally {
        setPoisLoading(false);
      }
    } else if (!shouldFetch || typesToFetch.length === 0) {
      setViewportPOIs([]);
    }
  }, [showStreamGauges, showPOIs, selectedPOITypes]);

  // Handle click on map features
  const onClick = useCallback((event) => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();

    const layersToQuery = ['destinations-layer', 'pois-layer', 'pois-circles', 'waterways-flowlines', 'waterways-waterbodies'].filter(id => map.getLayer(id));

    const features = map.queryRenderedFeatures(event.point, {
      layers: layersToQuery
    });

    const feature = features?.[0];
    if (!feature) return;

    if (feature.layer?.id === 'destinations-layer') {
      const destId = feature.properties?.id;
      const dest = destinations.find(d => d.id === destId);
      if (dest) {
        setPopupInfo({
          longitude: dest.longitude,
          latitude: dest.latitude,
          destination: dest
        });
        if (onDestinationSelect) {
          onDestinationSelect(dest);
        }
      }
    } else if (feature.layer?.id === 'pois-layer' || feature.layer?.id === 'pois-circles') {
      const props = feature.properties;
      let poiProperties = {};
      try {
        poiProperties = JSON.parse(props.properties_json || '{}');
      } catch (e) {  }

      // Stream gauge POI
      if (props.type === 'stream_gauge') {
        const siteId = poiProperties.site_id || props.source_id;
        const siteName = props.name;

        if (gaugeConditionsCache[siteId]) {
          setPopupInfo({
            longitude: feature.geometry.coordinates[0],
            latitude: feature.geometry.coordinates[1],
            streamGauge: gaugeConditionsCache[siteId]
          });
          return;
        }

        setPopupInfo({
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          streamGauge: {
            site_id: siteId,
            site_name: siteName,
            loading: true
          }
        });

        setGaugeConditionsLoading(true);
        Promise.all([
          axios.get(`${API}/streamflow/site/${siteId}`),
          axios.get(`${API}/streamflow/trends/${siteId}`)
        ])
          .then(([conditionsResponse, trendsResponse]) => {
            const data = conditionsResponse.data;
            const trendsData = trendsResponse.data;
            const gaugeData = {
              site_id: siteId,
              site_name: data.site_name || siteName,
              discharge_cfs: data.discharge_cfs,
              gage_height_ft: data.gage_height_ft,
              water_temp_f: data.water_temp_f,
              condition: data.condition,
              condition_label: data.condition_label,
              color: getConditionColor(data.condition),
              sparkline_data: trendsData.sparkline_data || [],
              trend_7d: trendsData.trends?.['7_day'] || 'stable',
              stats_7d: trendsData.stats_7d,
              loading: false
            };

            setGaugeConditionsCache(prev => ({
              ...prev,
              [siteId]: gaugeData
            }));

            setPopupInfo(prev => {
              if (prev?.streamGauge?.site_id === siteId) {
                return { ...prev, streamGauge: gaugeData };
              }
              return prev;
            });
          })
          .catch(error => {
            setPopupInfo(prev => {
              if (prev?.streamGauge?.site_id === siteId) {
                return {
                  ...prev,
                  streamGauge: {
                    site_id: siteId,
                    site_name: siteName,
                    loading: false,
                    error: true
                  }
                };
              }
              return prev;
            });
          })
          .finally(() => {
            setGaugeConditionsLoading(false);
          });
        return;
      }

      // Regular POI click
      const poiData = {
        id: props.id,
        name: props.name,
        type: props.type,
        state: props.state,
        county: props.county,
        source: props.source,
        description: props.description,
        properties: poiProperties,
        latitude: feature.geometry.coordinates[1],
        longitude: feature.geometry.coordinates[0]
      };

      setPopupInfo({
        longitude: feature.geometry.coordinates[0],
        latitude: feature.geometry.coordinates[1],
        poi: poiData
      });
    } else if (feature.layer?.id === 'waterways-flowlines' || feature.layer?.id === 'waterways-waterbodies') {
      const props = feature.properties;
      const permanentId = props.Permanent_Identifier;
      const gnisId = props.GNIS_ID;
      const gnisName = props.GNIS_Name;
      const isLake = feature.layer.id === 'waterways-waterbodies';

      // Highlight the selected waterway
      if (permanentId) {
        if (isLake) {
          if (map.getLayer('waterbody-highlight')) {
            map.setFilter('waterbody-highlight', ['==', 'Permanent_Identifier', permanentId]);
          }
          if (map.getLayer('waterbody-highlight-outline')) {
            map.setFilter('waterbody-highlight-outline', ['==', 'Permanent_Identifier', permanentId]);
          }
          if (map.getLayer('waterway-highlight')) {
            map.setFilter('waterway-highlight', ['==', 'Permanent_Identifier', '']);
          }
        } else {
          if (map.getLayer('waterway-highlight')) {
            map.setFilter('waterway-highlight', ['==', 'Permanent_Identifier', permanentId]);
          }
          if (map.getLayer('waterbody-highlight')) {
            map.setFilter('waterbody-highlight', ['==', 'Permanent_Identifier', '']);
          }
          if (map.getLayer('waterbody-highlight-outline')) {
            map.setFilter('waterbody-highlight-outline', ['==', 'Permanent_Identifier', '']);
          }
        }
      }

      let clickLng, clickLat;
      if (feature.geometry.type === 'Point') {
        [clickLng, clickLat] = feature.geometry.coordinates;
      } else {
        clickLng = event.lngLat.lng;
        clickLat = event.lngLat.lat;
      }

      const waterwayData = {
        permanent_identifier: permanentId,
        name: gnisName || 'Unnamed Waterway',
        gnis_id: gnisId,
        length_km: props.LengthKM,
        length_mi: props.LengthKM ? (props.LengthKM * 0.621371).toFixed(2) : null,
        fcode: props.FCode,
        type: isLake ? 'lake' : 'stream',
        area_sqkm: props.AreaSqKm,
        geometry: feature.geometry,
        coordinates: { lng: clickLng, lat: clickLat }
      };

      if (onWaterwayClick) {
        onWaterwayClick(waterwayData);
      }

      // If this is a NAMED waterway (has GNIS_ID), open the detail panel instead of popup
      if (gnisId && gnisName) {
        setPopupInfo(null);
        setRiverPanelData({
          gnisId,
          waterwayData,
        });
      } else {
        // For unnamed waterways, keep the popup behavior
        setPopupInfo({
          longitude: clickLng,
          latitude: clickLat,
          waterway: waterwayData
        });

        // Fetch nearby POIs for unnamed waterways
        axios.get(`${API}/pois/nearby`, {
          params: { lat: clickLat, lng: clickLng, radius_km: 15, types: 'stream_gauge', limit: 5 }
        }).then(async (response) => {
          const nearbyGauges = response.data || [];
          const gaugesWithConditions = await Promise.all(
            nearbyGauges.slice(0, 3).map(async (gauge) => {
              const siteId = gauge.source_id || gauge.properties?.site_id;
              if (!siteId) return gauge;
              if (gaugeConditionsCache[siteId]) {
                return { ...gauge, conditions: gaugeConditionsCache[siteId] };
              }
              try {
                const [flowRes] = await Promise.all([
                  axios.get(`${API}/streamflow/site/${siteId}`)
                ]);
                const conditions = flowRes.data;
                setGaugeConditionsCache(prev => ({ ...prev, [siteId]: conditions }));
                return { ...gauge, conditions };
              } catch {
                return gauge;
              }
            })
          );
          setPopupInfo(prev => {
            if (prev?.waterway?.coordinates?.lat?.toFixed(3) === clickLat.toFixed(3)) {
              return { ...prev, waterway: { ...prev.waterway, nearbyGauges: gaugesWithConditions } };
            }
            return prev;
          });
        });

        axios.get(`${API}/pois/nearby`, {
          params: { lat: clickLat, lng: clickLng, radius_km: 10, exclude_types: 'stream_gauge', limit: 6 }
        }).then(response => {
          const nearbyPois = response.data || [];
          setPopupInfo(prev => {
            if (prev?.waterway?.coordinates?.lat?.toFixed(3) === clickLat.toFixed(3)) {
              return { ...prev, waterway: { ...prev.waterway, nearbyPois } };
            }
            return prev;
          });
        });
      }

    }
  }, [destinations, onDestinationSelect, onWaterwayClick, gaugeConditionsCache, mapRef, setPopupInfo, setRiverPanelData]);

  // Change cursor on hover
  const onMouseEnter = useCallback(() => {
    if (mapRef.current) mapRef.current.getCanvas().style.cursor = 'pointer';
  }, [mapRef]);

  const onMouseLeave = useCallback(() => {
    if (mapRef.current) mapRef.current.getCanvas().style.cursor = '';
  }, [mapRef]);

  // Report bounds when map moves and fetch viewport data - debounced
  const handleMoveEnd = useCallback((onBoundsChange) => {
    if (mapRef.current) {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      requestAnimationFrame(() => {
        if (mapRef.current) {
          const map = mapRef.current.getMap();
          const bounds = map.getBounds();
          const zoom = map.getZoom();
          const center = map.getCenter();

          if (onBoundsChange) {
            onBoundsChange({
              north: bounds.getNorth(),
              south: bounds.getSouth(),
              east: bounds.getEast(),
              west: bounds.getWest(),
              zoom: zoom,
              center: { lat: center.lat, lng: center.lng }
            });
          }

          fetchTimeoutRef.current = setTimeout(() => {
            fetchViewportData(bounds, zoom);
          }, 300);
        }
      });
    }
  }, [mapRef, fetchViewportData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []);

  return {
    viewportPOIs,
    setViewportPOIs,
    poisLoading,
    gaugeConditionsLoading,
    gaugeConditionsCache,
    riverDataLoading,
    riverDataCache,
    fetchViewportData,
    onClick,
    onMouseEnter,
    onMouseLeave,
    handleMoveEnd,
    lastPOITypesRef,
  };
}
